// ═══════════════════════════════════════════════════════════════════
//  TOQUM · SAMPLE EVALUATION
//  A report per sample stage, cross-checked against the Risk Assessment
//  (J-03) raised at PPM. Can be evaluated by the QA alone, or filled by
//  the supplier (report + images uploaded) and validated by the QA.
//  Points from the evaluation AND the RAS can be pushed into a
//  follow-up checklist used during production and inspection.
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useMemo } from 'react';
import {
  T, uid, today, num, Page, CardRow, Split, Section, Field, Grid, Btn, DataTable, TabBar,
  Tag, Badge, StatCard, DynamicTable, Collapse, Modal, Toast, TriState, printHTML,
  uploadFile, isManager, hasRight,
} from './Shared';
import { RISK_COLS } from './FormatSchemas';

export const SAMPLE_STAGES = [
  'Proto Sample','Fit Sample','Counter Sample','Size Set','PP Sample',
  'Pilot Run','Photo Sample','Shipment/TOP Sample','Wash Standard','Reference Sample',
];

// Evaluation criteria — grouped, each scored and commentable
export const EVAL_GROUPS = [
  { key:'construction', title:'Construction & Workmanship', points:[
    'Seam construction as per techpack','Stitch type & SPI correct','Seam strength / puckering',
    'Symmetry (pockets, panels, collar)','Bartack & reinforcement placement',
    'Topstitch evenness & margin','Hem & bottom finish','Zipper / closure function',
    'Lining & drawcord finish','Thread colour & tension',
  ]},
  { key:'measurement', title:'Measurement & Fit', points:[
    'Key POMs within tolerance','Graded sizes consistent','After-wash measurement holds',
    'Pattern shrinkage compensation applied','Fit comments from previous sample addressed',
  ]},
  { key:'fabric', title:'Fabric & Material', points:[
    'Fabric matches approved swatch','GSM within tolerance','Shade vs approved shade band',
    'Hand feel acceptable','Shrinkage test result acceptable','Fabric defects absent in sample',
  ]},
  { key:'trims', title:'Trims & Accessories', points:[
    'All trims as per approved trim card','Labels correct & positioned','Hangtag / price ticket correct',
    'Button / rivet pull test passed','Metal-free / nickel test done','Care label instructions correct',
  ]},
  { key:'valueadd', title:'Print / Embroidery / Wash', points:[
    'Print placement & size correct','Print hand feel & cracking test','Embroidery density & backing',
    'Wash shade matches standard','Wash effect (whisker/grinding) as approved','No damage from wet process',
  ]},
  { key:'safety', title:'Safety & Compliance', points:[
    'Needle detection passed','Sharp objects absent','Drawcord safety compliance',
    'Fire warning label present','Chemical / test report available',
  ]},
];

const VERDICTS = ['Approved','Approved with Comments','Rejected — Resubmit','Pending Review'];
const SCORE_OPTIONS = ['OK','NG','NA'];

export default function SampleEvaluation({ db, setDb, saveToDB, currentUser }) {
  const TABS = ['DASHBOARD','EVALUATIONS','NEW EVALUATION','RAS COMPARISON','FOLLOW-UP CHECKLISTS'];
  const [tab, setTab] = useState('DASHBOARD');
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState(null);
  const notify = (m,t='info') => { setToast({ msg:m, type:t }); setTimeout(()=>setToast(null), 4000); };

  const evals = db.sampleEvaluations || [];
  const pos   = db.pos || [];
  const canEdit = hasRight(currentUser, 'QA', 'entry') || isManager(currentUser);

  // Risk Assessment rows filed against a PO (from the J-03 record)
  const rasFor = (poNo) => {
    const rec = (db.qualityRecords||[]).find(r => r.poNo===poNo && r.format==='RISK');
    return rec?.data?.rows || [];
  };

  // ═══ DASHBOARD ═══
  const Dashboard = () => {
    const byStage = SAMPLE_STAGES.map(s => ({
      stage: s,
      total: evals.filter(e=>e.stage===s).length,
      approved: evals.filter(e=>e.stage===s && e.verdict==='Approved').length,
      rejected: evals.filter(e=>e.stage===s && e.verdict?.startsWith('Rejected')).length,
    })).filter(x=>x.total);
    const pending = evals.filter(e=>e.verdict==='Pending Review');
    const supplierFilled = evals.filter(e=>e.filledBy==='Supplier');
    return (
      <div>
        <CardRow min={200}>
          <StatCard label="Total Evaluations" value={evals.length} color={T.primary} />
          <StatCard label="Approved" value={evals.filter(e=>e.verdict==='Approved').length} color={T.success} />
          <StatCard label="With Comments" value={evals.filter(e=>e.verdict==='Approved with Comments').length} color={T.warning} />
          <StatCard label="Rejected" value={evals.filter(e=>e.verdict?.startsWith('Rejected')).length} color={T.danger} />
          <StatCard label="Awaiting QA Validation" value={pending.length} color={T.purple} />
          <StatCard label="Supplier-filled" value={supplierFilled.length}
            sub={`${supplierFilled.filter(e=>e.validatedBy).length} validated`} color={T.teal} />
        </CardRow>
        <Split min={420}>
          <Section title="Evaluations by Sample Stage">
            <DataTable columns={['Sample Stage','Total','Approved','Rejected','Pass %']} rows={byStage.map((b,i)=>({...b,id:i}))}
              renderCell={(r,c)=>({ 'Sample Stage':<b>{r.stage}</b>, Total:r.total, Approved:r.approved,
                Rejected:r.rejected,
                'Pass %':<b style={{ color: r.total && r.approved/r.total>=0.8 ? T.success : T.warning }}>
                  {r.total ? Math.round(r.approved/r.total*100) : 0}%</b> }[c])}
              emptyMsg="No sample evaluations recorded yet." />
          </Section>
          <Section title="Awaiting QA Validation">
            <DataTable columns={['Date','PO','Supplier','Stage','Filled By']} rows={pending}
              renderCell={(r,c)=>({ Date:r.date, PO:r.poNo, Supplier:r.supplier, Stage:r.stage, 'Filled By':r.filledBy }[c])}
              actions={r => <Btn small label="Validate" onClick={()=>{ setDetail(r); setTab('EVALUATIONS'); }} />}
              emptyMsg="Nothing waiting for validation." />
          </Section>
        </Split>
      </div>
    );
  };

  // ═══ NEW / EDIT EVALUATION ═══
  const EvalForm = ({ existing, onDone }) => {
    const [f, setF] = useState(existing || {
      poNo:'', stage:'PP Sample', date:today(), sampleRef:'', colour:'', size:'',
      filledBy:'QA', submittedBy:currentUser.name, verdict:'Pending Review',
      scores:{}, comments:{}, findings:[], images:[], reportFile:null,
      overallComments:'', rasLinks:[],
    });
    const set = (k,v) => setF(p=>({ ...p, [k]:v }));
    const po = pos.find(p=>p.poNo===f.poNo);
    const ras = rasFor(f.poNo);

    const setScore = (g,pt,v) => setF(p=>({ ...p, scores:{ ...p.scores, [`${g}|${pt}`]:v } }));
    const setComment = (g,pt,v) => setF(p=>({ ...p, comments:{ ...p.comments, [`${g}|${pt}`]:v } }));

    const stats = useMemo(() => {
      let ok=0, ng=0, na=0;
      EVAL_GROUPS.forEach(g=>g.points.forEach(pt=>{
        const v = f.scores[`${g.key}|${pt}`];
        if (v==='OK') ok++; else if (v==='NG') ng++; else if (v==='NA') na++;
      }));
      const scored = ok+ng;
      return { ok, ng, na, scored, pct: scored ? Math.round(ok/scored*100) : 0 };
    }, [f.scores]);

    const addImages = async (e) => {
      const files = Array.from(e.target.files||[]); e.target.value='';
      for (const file of files) {
        const up = await uploadFile(file, { category:'sampleEval', poNo:f.poNo,
          supplier:po?.supplier, docType:'Sample Image', stage:f.stage, uploadedBy:currentUser.name });
        setF(p=>({ ...p, images:[...p.images, { name:up.name, url:up.url, caption:'', offline:up.offline }] }));
      }
      notify(`${files.length} image(s) attached.`, 'success');
    };
    const addReport = async (e) => {
      const file = e.target.files[0]; e.target.value=''; if (!file) return;
      const up = await uploadFile(file, { category:'sampleEval', poNo:f.poNo,
        supplier:po?.supplier, docType:'Sample Evaluation Report', uploadedBy:currentUser.name });
      set('reportFile', { name:up.name, url:up.url, offline:up.offline });
      notify('Report attached.', 'success');
    };

    const save = async (verdict) => {
      if (!f.poNo) return notify('Select a PO', 'error');
      const entry = { ...f, id: f.id || uid(), verdict,
        supplier: po?.supplier, styleNo: po?.styleNo, brand: po?.brand,
        okCount: stats.ok, ngCount: stats.ng, scorePct: stats.pct,
        validatedBy: verdict !== 'Pending Review' && f.filledBy === 'Supplier' ? currentUser.name : f.validatedBy,
        validatedOn: verdict !== 'Pending Review' && f.filledBy === 'Supplier' ? today() : f.validatedOn,
      };
      setDb(p=>({ ...p, sampleEvaluations:[...evals.filter(x=>x.id!==entry.id), entry] }));
      await saveToDB('sampleEvaluation', entry);
      notify(`Sample evaluation saved — ${verdict}.`, 'success');
      onDone && onDone(entry);
    };

    const print = () => printHTML(`Sample Evaluation ${f.poNo}`, `
      <h1>TOQUM · Sample Evaluation Report</h1>
      <table><tr><td><b>PO:</b> ${f.poNo}</td><td><b>Style:</b> ${po?.styleNo||''}</td>
        <td><b>Supplier:</b> ${po?.supplier||''}</td><td><b>Buyer:</b> ${po?.brand||''}</td></tr>
        <tr><td><b>Sample Stage:</b> ${f.stage}</td><td><b>Sample Ref:</b> ${f.sampleRef}</td>
        <td><b>Colour:</b> ${f.colour}</td><td><b>Size:</b> ${f.size}</td></tr>
        <tr><td><b>Date:</b> ${f.date}</td><td><b>Filled By:</b> ${f.filledBy} (${f.submittedBy})</td>
        <td><b>Score:</b> ${stats.pct}% (${stats.ok} OK / ${stats.ng} NG)</td>
        <td><b>Verdict:</b> ${f.verdict}</td></tr></table>
      ${EVAL_GROUPS.map(g=>`<h3>${g.title}</h3><table>
        <tr><th>Point</th><th>Result</th><th>Comment</th></tr>
        ${g.points.map(pt=>`<tr><td>${pt}</td><td>${f.scores[`${g.key}|${pt}`]||'—'}</td>
          <td>${f.comments[`${g.key}|${pt}`]||''}</td></tr>`).join('')}</table>`).join('')}
      ${f.findings?.length?`<h3>Additional Findings</h3><table>
        <tr><th>Finding</th><th>Severity</th><th>Action Required</th><th>Responsible</th><th>Due</th></tr>
        ${f.findings.map(x=>`<tr><td>${x.finding||''}</td><td>${x.severity||''}</td>
          <td>${x.action||''}</td><td>${x.responsible||''}</td><td>${x.dueDate||''}</td></tr>`).join('')}</table>`:''}
      <p><b>Overall Comments:</b> ${(f.overallComments||'').replace(/\n/g,'<br/>')}</p>
      ${f.images?.length?`<h3>Sample Images</h3><div class="imgs">
        ${f.images.map(i=>`<div><img src="${i.url}"/><div style="font-size:8px">${i.caption||i.name}</div></div>`).join('')}</div>`:''}
      <table class="sign"><tr><th>Evaluated By</th><th>Validated By (QA)</th><th>Quality Manager</th></tr>
        <tr><td>${f.submittedBy}</td><td>${f.validatedBy||''}</td><td></td></tr></table>`);

    return (
      <div>
        <Section title={existing ? `Sample Evaluation — ${f.stage} · PO ${f.poNo}` : 'New Sample Evaluation'}
          actions={<span style={{ display:'flex', gap:6 }}>
            <Btn small outline label="🖨 Print" onClick={print} />
            {onDone && <Btn small color="#64748b" label="← Back" onClick={()=>onDone(null)} />}
          </span>}>
          <Grid cols={4}>
            <Field label="PO *" value={f.poNo} onChange={e=>set('poNo',e.target.value)} options={pos.map(p=>p.poNo)} />
            <Field label="Sample Stage *" value={f.stage} onChange={e=>set('stage',e.target.value)} options={SAMPLE_STAGES} />
            <Field label="Evaluation Date" type="date" value={f.date} onChange={e=>set('date',e.target.value)} />
            <Field label="Sample Reference" value={f.sampleRef} onChange={e=>set('sampleRef',e.target.value)} />
            <Field label="Colour" value={f.colour} onChange={e=>set('colour',e.target.value)}
              options={po ? [...new Set((po.matrix||[]).map(m=>m.color).filter(Boolean))] : null} />
            <Field label="Size" value={f.size} onChange={e=>set('size',e.target.value)}
              options={po ? [...new Set((po.matrix||[]).map(m=>m.size).filter(Boolean))] : null} />
            <Field label="Evaluated By" value={f.filledBy} onChange={e=>set('filledBy',e.target.value)}
              options={['QA','Supplier']} />
            <Field label="Name" value={f.submittedBy} onChange={e=>set('submittedBy',e.target.value)} />
          </Grid>
          {po && <div style={{ fontSize:12, color:T.textSub }}>
            Style <b>{po.styleNo}</b> · Supplier <b>{po.supplier}</b> · Buyer <b>{po.brand}</b>
            {ras.length>0 && <> · <Tag label={`${ras.length} risk points from PPM available below`} color={T.purple} /></>}
          </div>}
          {f.filledBy==='Supplier' && (
            <div style={{ background:'#f5f3ff', border:`1px solid ${T.purple}40`, borderRadius:8,
              padding:10, marginTop:10, fontSize:12 }}>
              Supplier-filled evaluation. Attach the supplier's report and images below; the QA validates
              it before the verdict counts.
              <div style={{ display:'flex', gap:14, marginTop:8, alignItems:'center', flexWrap:'wrap' }}>
                <label style={{ fontSize:12, fontWeight:700, color:T.purple, cursor:'pointer' }}>
                  📄 Upload Supplier Report {f.reportFile && `✓ ${f.reportFile.name}`}
                  <input type="file" hidden onChange={addReport} />
                </label>
              </div>
            </div>
          )}
        </Section>

        {EVAL_GROUPS.map(g=>(
          <Collapse key={g.key} title={g.title} defaultOpen={g.key==='construction'}
            badge={`${g.points.filter(pt=>f.scores[`${g.key}|${pt}`]).length}/${g.points.length} checked`}>
            {g.points.map(pt=>{
              const v = f.scores[`${g.key}|${pt}`];
              return (
                <div key={pt} style={{ display:'flex', gap:10, alignItems:'center', padding:'5px 0',
                  borderBottom:'1px solid #f8fafc', flexWrap:'wrap' }}>
                  <span style={{ flex:'1 1 240px', fontSize:12 }}>{pt}</span>
                  <TriState value={v||''} onChange={val=>setScore(g.key,pt,val)} options={SCORE_OPTIONS} />
                  <input value={f.comments[`${g.key}|${pt}`]||''} onChange={e=>setComment(g.key,pt,e.target.value)}
                    placeholder={v==='NG' ? 'Required — what is wrong?' : 'comment'}
                    style={{ flex:'1 1 220px', padding:'5px 8px', fontSize:11,
                      border:`1px solid ${v==='NG' && !f.comments[`${g.key}|${pt}`] ? T.danger : '#cbd5e1'}`,
                      borderRadius:4 }} />
                </div>
              );
            })}
          </Collapse>
        ))}

        <Section title="Additional Findings & Required Actions">
          <DynamicTable dense
            columns={[
              { label:'Finding', key:'finding', type:'textarea', width:'240px' },
              { label:'Severity', key:'severity', type:'select', options:['Critical','Major','Minor','Observation'] },
              { label:'Action Required', key:'action', type:'textarea', width:'220px' },
              { label:'Responsible', key:'responsible' },
              { label:'Due Date', key:'dueDate', type:'date' },
              { label:'Carry to Production Checklist', key:'toChecklist', type:'select', options:['Yes','No'] },
            ]}
            data={f.findings} setData={v=>set('findings',v)}
            onAdd={()=>set('findings',[...f.findings,{ severity:'Major', toChecklist:'Yes', dueDate:today() }])} />
        </Section>

        {ras.length>0 && (
          <Section title="Risk Assessment (J-03) raised at PPM — tick the points this sample confirms or clears">
            <DataTable columns={['Department','Process','Potential Issue','Preventive Action','Sample Result']}
              rows={ras.map((r,i)=>({ ...r, id:i }))}
              renderCell={(r,c)=>({ Department:r.department, Process:r.process, 'Potential Issue':r.issue,
                'Preventive Action':r.action,
                'Sample Result':<TriState options={['Cleared','Still At Risk','NA']}
                  value={(f.rasLinks.find(x=>x.idx===r.id)||{}).result||''}
                  onChange={val=>set('rasLinks',[...f.rasLinks.filter(x=>x.idx!==r.id),
                    { idx:r.id, issue:r.issue, action:r.action, department:r.department, result:val }])} /> }[c])} />
          </Section>
        )}

        <Section title="Images & Overall Verdict">
          <label style={{ fontSize:12, fontWeight:700, color:T.primary, cursor:'pointer' }}>
            📷 Attach Sample Images ({f.images.length})
            <input type="file" multiple accept="image/*" hidden onChange={addImages} />
          </label>
          {f.images.length>0 && (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',
              gap:10, marginTop:10 }}>
              {f.images.map((im,i)=>(
                <div key={i} style={{ border:`1px solid ${T.border}`, borderRadius:8, overflow:'hidden' }}>
                  <img src={im.url} alt={im.name} style={{ width:'100%', height:100, objectFit:'cover' }} />
                  <input value={im.caption} placeholder="caption"
                    onChange={e=>set('images', f.images.map((x,ix)=>ix===i?{...x,caption:e.target.value}:x))}
                    style={{ width:'100%', border:'none', borderTop:`1px solid ${T.border}`, padding:5, fontSize:10 }} />
                </div>
              ))}
            </div>
          )}
          <Field label="Overall Comments" type="textarea" value={f.overallComments}
            onChange={e=>set('overallComments',e.target.value)} />
          <div style={{ display:'flex', gap:18, alignItems:'center', flexWrap:'wrap',
            background: stats.ng ? '#fdab3d12' : '#00c87512', padding:'10px 14px', borderRadius:8,
            border:`1px solid ${stats.ng ? T.warning : T.success}`, marginBottom:12 }}>
            <b style={{ fontSize:13 }}>Score: {stats.pct}% — {stats.ok} OK · {stats.ng} NG · {stats.na} N/A</b>
            {stats.ng>0 && <span style={{ color:T.warning, fontSize:12, fontWeight:700 }}>
              {stats.ng} failing point(s) should carry into the production follow-up checklist</span>}
          </div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {VERDICTS.map(v=>(
              <Btn key={v} label={v} onClick={()=>save(v)}
                color={v==='Approved'?T.success:v.startsWith('Rejected')?T.danger:v==='Pending Review'?T.textSub:T.warning}
                outline={v!=='Approved'} />
            ))}
          </div>
        </Section>
      </div>
    );
  };

  // ═══ EVALUATIONS LIST ═══
  const EvalList = () => {
    if (detail) return <EvalForm existing={detail} onDone={()=>setDetail(null)} />;
    return (
      <Section title="Sample Evaluation Register">
        <DataTable columns={['Date','PO','Style','Supplier','Stage','Filled By','Score','NG','Verdict','Validated']}
          rows={evals.slice().reverse()}
          renderCell={(r,c)=>({
            Date:r.date, PO:r.poNo, Style:r.styleNo, Supplier:r.supplier, Stage:<b>{r.stage}</b>,
            'Filled By': <Tag label={r.filledBy} color={r.filledBy==='Supplier'?T.purple:T.teal} />,
            Score:`${r.scorePct||0}%`,
            NG: r.ngCount ? <b style={{ color:T.danger }}>{r.ngCount}</b> : '0',
            Verdict:<Badge status={r.verdict==='Approved'?'Approved':r.verdict?.startsWith('Rejected')?'Rejected':'Pending'} />,
            Validated: r.validatedBy || (r.filledBy==='QA' ? '—' : <Tag label="awaiting QA" color={T.warning}/>),
          }[c])}
          actions={r => <Btn small outline label="Open" onClick={()=>setDetail(r)} />}
          emptyMsg="No evaluations yet." />
      </Section>
    );
  };

  // ═══ RAS COMPARISON ═══
  const RasComparison = () => {
    const [poNo, setPoNo] = useState('');
    const ras = rasFor(poNo);
    const poEvals = evals.filter(e=>e.poNo===poNo);
    const linkFor = (idx) => {
      const withLink = poEvals.filter(e=>(e.rasLinks||[]).some(l=>l.idx===idx));
      const latest = withLink.slice(-1)[0];
      return latest ? { ...(latest.rasLinks||[]).find(l=>l.idx===idx), stage:latest.stage, date:latest.date } : null;
    };
    const cleared = ras.filter((_,i)=>linkFor(i)?.result==='Cleared').length;
    const atRisk  = ras.filter((_,i)=>linkFor(i)?.result==='Still At Risk').length;
    const untested = ras.length - cleared - atRisk;

    return (
      <div>
        <Section title="Compare Sample Evaluation against the PPM Risk Assessment">
          <Grid cols={4}>
            <Field label="PO" value={poNo} onChange={e=>setPoNo(e.target.value)} options={pos.map(p=>p.poNo)} />
          </Grid>
          {poNo && ras.length===0 && <div style={{ fontSize:12, color:T.warning }}>
            No Risk Assessment (J-03) filed for this PO yet. Fill it in QA → PO Quality File first.
          </div>}
        </Section>
        {ras.length>0 && (
          <>
            <CardRow min={200}>
              <StatCard label="Risk Points Raised at PPM" value={ras.length} color={T.primary} />
              <StatCard label="Cleared by Sample" value={cleared} color={T.success} />
              <StatCard label="Still At Risk" value={atRisk} color={atRisk?T.danger:T.textSub} />
              <StatCard label="Not Yet Tested" value={untested} color={untested?T.warning:T.success} />
              <StatCard label="Evaluations on this PO" value={poEvals.length} color={T.teal} />
            </CardRow>
            <Section title="Risk Point vs Sample Result">
              <DataTable columns={['#','Department','Process','Potential Issue','Preventive Action','Sample Result','Confirmed In']}
                rows={ras.map((r,i)=>({ ...r, id:i }))}
                renderCell={(r,c)=>{
                  const l = linkFor(r.id);
                  return ({ '#':r.id+1, Department:r.department, Process:r.process,
                    'Potential Issue':r.issue, 'Preventive Action':r.action,
                    'Sample Result': l
                      ? <Badge status={l.result==='Cleared'?'Passed':l.result==='Still At Risk'?'Failed':'Draft'} />
                      : <Tag label="not tested" color={T.warning} />,
                    'Confirmed In': l ? `${l.stage} (${l.date})` : '—' })[c];
                }} />
            </Section>
            {atRisk>0 && (
              <Section title="⚠ Still At Risk — these must carry into the production follow-up checklist">
                <DataTable columns={['Department','Potential Issue','Preventive Action']}
                  rows={ras.map((r,i)=>({ ...r, id:i })).filter(r=>linkFor(r.id)?.result==='Still At Risk')}
                  renderCell={(r,c)=>({ Department:r.department, 'Potential Issue':r.issue,
                    'Preventive Action':r.action }[c])} />
                <Btn label="Build Follow-up Checklist from these points" color={T.warning}
                  onClick={()=>{ buildChecklist(poNo); setTab('FOLLOW-UP CHECKLISTS'); }} />
              </Section>
            )}
          </>
        )}
      </div>
    );
  };

  // Build a checklist from RAS "still at risk" + all NG sample points + flagged findings
  const buildChecklist = async (poNo) => {
    const po = pos.find(p=>p.poNo===poNo);
    const ras = rasFor(poNo);
    const poEvals = evals.filter(e=>e.poNo===poNo);
    const items = [];

    poEvals.forEach(ev => {
      EVAL_GROUPS.forEach(g => g.points.forEach(pt => {
        if (ev.scores?.[`${g.key}|${pt}`] === 'NG') {
          items.push({ source:`Sample — ${ev.stage}`, area:g.title, point:pt,
            detail: ev.comments?.[`${g.key}|${pt}`] || '', severity:'Major',
            checkAt:'Inline', status:'Open' });
        }
      }));
      (ev.findings||[]).filter(x=>x.toChecklist!=='No').forEach(x => {
        items.push({ source:`Sample finding — ${ev.stage}`, area:'Finding', point:x.finding,
          detail:x.action, severity:x.severity||'Major', responsible:x.responsible,
          checkAt:'Inline', status:'Open' });
      });
      (ev.rasLinks||[]).filter(l=>l.result==='Still At Risk').forEach(l => {
        items.push({ source:'Risk Assessment (PPM)', area:l.department, point:l.issue,
          detail:l.action, severity:'Critical', checkAt:'Cutting', status:'Open' });
      });
    });

    // Any RAS point never tested also goes on the list
    ras.forEach(r => {
      const tested = poEvals.some(e=>(e.rasLinks||[]).some(l=>l.issue===r.issue));
      if (!tested) items.push({ source:'Risk Assessment (PPM) — untested', area:r.department,
        point:r.issue, detail:r.action, severity:'Major', checkAt:'Inline', status:'Open' });
    });

    if (!items.length) return notify('Nothing to add — no failing points or open risks for this PO.', 'warn');

    const entry = { id: uid(), poNo, styleNo: po?.styleNo, supplier: po?.supplier, brand: po?.brand,
      createdBy: currentUser.name, createdOn: today(), items, status:'Active' };
    setDb(p=>({ ...p, followUpChecklists:[...(p.followUpChecklists||[]), entry] }));
    await saveToDB('followUpChecklist', entry);
    notify(`Checklist created with ${items.length} point(s) to follow during production.`, 'success');
  };

  // ═══ FOLLOW-UP CHECKLISTS ═══
  const Checklists = () => {
    const lists = db.followUpChecklists || [];
    const [poNo, setPoNo] = useState('');
    const update = async (list, idx, patch) => {
      const items = list.items.map((it,i)=> i===idx ? { ...it, ...patch } : it);
      const upd = { ...list, items,
        status: items.every(i=>i.status==='Verified') ? 'Completed' : 'Active' };
      setDb(p=>({ ...p, followUpChecklists: lists.map(l=>l.id===list.id?upd:l) }));
      await saveToDB('followUpChecklist', upd);
    };
    const printList = (l) => printHTML(`Follow-up Checklist ${l.poNo}`, `
      <h1>TOQUM · Production & Inspection Follow-up Checklist</h1>
      <table><tr><td><b>PO:</b> ${l.poNo}</td><td><b>Style:</b> ${l.styleNo||''}</td>
        <td><b>Supplier:</b> ${l.supplier||''}</td><td><b>Created:</b> ${l.createdOn}</td></tr></table>
      <table><tr><th>#</th><th>Source</th><th>Area</th><th>Point to Check</th><th>Detail / Action</th>
        <th>Severity</th><th>Check At</th><th>Status</th><th>Verified By</th></tr>
        ${l.items.map((it,i)=>`<tr><td>${i+1}</td><td>${it.source}</td><td>${it.area||''}</td>
          <td>${it.point||''}</td><td>${it.detail||''}</td><td>${it.severity||''}</td>
          <td>${it.checkAt||''}</td><td>${it.status||''}</td><td>${it.verifiedBy||''}</td></tr>`).join('')}</table>
      <p class="note">Every point on this list originated from a sample evaluation failure or an open
      risk from the pre-production meeting. Verify each one during production before final inspection.</p>`);

    return (
      <div>
        <Section title="Build a Follow-up Checklist"
          actions={<Btn small color={T.success} label="Build from Sample + RAS"
            onClick={()=>poNo ? buildChecklist(poNo) : notify('Select a PO first','warn')} />}>
          <Grid cols={4}>
            <Field label="PO" value={poNo} onChange={e=>setPoNo(e.target.value)} options={pos.map(p=>p.poNo)} />
          </Grid>
          <div style={{ fontSize:11.5, color:T.textSub }}>
            Pulls every NG point and flagged finding from that PO's sample evaluations, plus any risk
            from the PPM that is still open or was never tested against a sample.
          </div>
        </Section>
        {lists.slice().reverse().map(l=>{
          const done = l.items.filter(i=>i.status==='Verified').length;
          return (
            <Collapse key={l.id} defaultOpen={l.status==='Active'}
              title={`PO ${l.poNo} · ${l.supplier} — ${l.items.length} points`}
              badge={`${done}/${l.items.length} verified`}
              color={l.status==='Completed'?T.success:T.warning}>
              <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:6 }}>
                <Btn small outline label="🖨 Print" onClick={()=>printList(l)} />
              </div>
              <DataTable columns={['#','Source','Area','Point to Check','Detail / Action','Severity','Check At','Status','Verified By']}
                rows={l.items.map((it,i)=>({ ...it, id:i }))}
                renderCell={(r,c)=>({
                  '#':r.id+1,
                  Source:<Tag label={r.source} color={r.source.includes('Risk')?T.purple:T.teal} />,
                  Area:r.area, 'Point to Check':<b>{r.point}</b>, 'Detail / Action':r.detail,
                  Severity:<Badge status={r.severity==='Critical'?'Failed':r.severity==='Major'?'Pending':'Draft'} />,
                  'Check At':<select value={r.checkAt||''} onChange={e=>update(l,r.id,{ checkAt:e.target.value })}
                    style={{ fontSize:11, padding:3 }}>
                    {['Cutting','Inline','Finishing','Pre-Final','Final'].map(o=><option key={o}>{o}</option>)}</select>,
                  Status:<select value={r.status||'Open'} onChange={e=>update(l,r.id,
                    { status:e.target.value, verifiedBy: e.target.value==='Verified' ? currentUser.name : r.verifiedBy,
                      verifiedOn: e.target.value==='Verified' ? today() : r.verifiedOn })}
                    style={{ fontSize:11, padding:3 }}>
                    {['Open','In Progress','Verified','Not Applicable'].map(o=><option key={o}>{o}</option>)}</select>,
                  'Verified By': r.verifiedBy ? `${r.verifiedBy} (${r.verifiedOn||''})` : '—',
                }[c])} />
            </Collapse>
          );
        })}
        {lists.length===0 && <Section title="Checklists">
          <span style={{ fontSize:12, color:T.textSub }}>
            None built yet. Select a PO above and build one once its sample evaluations are recorded.
          </span></Section>}
      </div>
    );
  };

  return (
    <Page title="SAMPLE EVALUATION"
      subtitle="Stage-wise sample reports · risk assessment comparison · production follow-up checklists">
      <TabBar tabs={TABS} active={tab} onChange={t=>{ setTab(t); setDetail(null); }} />
      {tab==='DASHBOARD'            && <Dashboard />}
      {tab==='EVALUATIONS'          && <EvalList />}
      {tab==='NEW EVALUATION'       && <EvalForm onDone={()=>setTab('EVALUATIONS')} />}
      {tab==='RAS COMPARISON'       && <RasComparison />}
      {tab==='FOLLOW-UP CHECKLISTS' && <Checklists />}
      <Toast msg={toast?.msg} type={toast?.type} onClose={()=>setToast(null)} />
    </Page>
  );
}
